import { DateFormat, formatBytes, formatDate } from "@nova/format"
import { useAppSelector } from "integrations/redux/hooks"
import Image from "next/image"
import { LayerPluginSpec, PluginSpec } from "plugins/plugin-registry"
import { FC, useMemo } from "react"
import { selectAncestorEntitiesByEntityId, selectEntityById } from "../slice"
import { setRenamingLayerId } from "../slice.new"
import { useDispatch } from "react-redux"
import {
    useInspectorPanelUsage,
    useNoUsage,
} from "features/map/inspector-panel/useInspectorPanelUsage"
import { SidePanelLayer, SidePanelLayerType } from "@nova/constants"
import { getGenericListItemProgress, StatusAndProgressData } from "./GenericLayerListItemProgress"
import { SidePanelStatus } from "@nova/component-side-panel-tab-container"
import * as Icons from "@nova/component-icons"
import { NovaSpinner } from "@nova/component-nova-spinner"
import { useIsEditor } from "tools/hooks/useIsEditor"
import { getGenericLayerNameComponent } from "./GenericLayerListItem"
import { useFullnameForUserId } from "features/data-management/users/storage"

export function getGenericAssetsCategoryHeader(spec: LayerPluginSpec) {
    const GenericAssetsCategoryHeader = ({ entityCount }: { entityCount: number }) => {
        return (
            <div className="w-full flex gap-2 items-center">
                <div className="w-7 h-7">
                    <Image
                        src={spec.ImageIconUrl || ""}
                        alt={spec.displayName}
                        width={28}
                        height={28}
                        className="w-full h-full object-contain"
                        priority
                    />
                </div>
                <h5 className="w-full text-gray-50 group-hover:text-gray-0">{spec.displayName}</h5>
                <div className="px-2 bg-gray-900 rounded-full">
                    <small className="xs text-gray-100">{entityCount}</small>
                </div>
            </div>
        )
    }
    return GenericAssetsCategoryHeader
}

export function getGenericAssetsListItem({
    spec,
    ProgressComponent = !!spec.useUploadStatusAndProgress
        ? getGenericListItemProgress({
              useStatusAndProgress: spec.useUploadStatusAndProgress,
              overrideTextColor: "text-gray-300",
          })
        : undefined,
    ThumbnailRender,
    onClick = () => {},
}: {
    spec: PluginSpec
    ProgressComponent?: FC<{ id: string; isVisible: boolean }>
    ThumbnailRender?: React.FC<{ id: string }>
    onClick?: (a: { id: string; e: React.MouseEvent }) => void
}) {
    const LayerNameComponent = getGenericLayerNameComponent(spec)
    const GenericAssetsListItem = ({ id }: { id: string }) => {
        const dispatch = useDispatch()
        const isEditor = useIsEditor()
        const entity = useAppSelector(selectEntityById(id))
        const ancestors = useAppSelector(selectAncestorEntitiesByEntityId(id))
        const isVisible = entity.visible && !ancestors.find((ancestor) => !ancestor.visible)
        const { fullName } = useFullnameForUserId(entity.createdBy?.userId)
        const useUsageHook =
            !entity.type || entity.type === SidePanelLayerType.Group
                ? useNoUsage
                : useInspectorPanelUsage
        const usage = useUsageHook(entity as SidePanelLayer, 1)
        const usageString = useMemo(() => {
            if (usage?.usageAmount) {
                return formatBytes(usage.usageAmount)
            }
            return ""
        }, [usage?.usageAmount])
        const statusData = spec.useUploadStatusAndProgress
            ? spec.useUploadStatusAndProgress(id)
            : (() => ({}) as StatusAndProgressData)()
        const ThumbnailStatusAndProgressIndicator = useMemo(() => {
            switch (statusData.status) {
                case SidePanelStatus.loading:
                case SidePanelStatus.uploading:
                case SidePanelStatus.singleFileUploading:
                    const SpinnerComponent = () => {
                        return (
                            <div className="w-full h-full flex items-center justify-center">
                                <NovaSpinner className="w-4 h-4" />
                            </div>
                        )
                    }
                    return SpinnerComponent
                case SidePanelStatus.failed:
                case SidePanelStatus.upload:
                case SidePanelStatus.empty:
                case SidePanelStatus.ready:
                case SidePanelStatus.queued:
                    const ImageComponent = () => {
                        return (
                            <div className="w-full h-full flex items-center justify-center">
                                <Icons.SmallIcon
                                    Icon={Icons.BrokenImage}
                                    iconClassName="text-white-300"
                                />
                            </div>
                        )
                    }
                    return ImageComponent
            }
        }, [statusData.status])
        return (
            <div className="flex flex-col w-full p-2 relative">
                <div
                    className="w-full flex items-center gap-3"
                    onClick={(e) => onClick({ id, e })}
                    onDoubleClick={() => {
                        if (!isEditor) return
                        dispatch(setRenamingLayerId({ id }))
                    }}
                >
                    <div className="relative h-16 w-12 shrink-0 bg-gray-600 rounded-lg overflow-hidden">
                        {ThumbnailStatusAndProgressIndicator ? (
                            <ThumbnailStatusAndProgressIndicator />
                        ) : ThumbnailRender ? (
                            <ThumbnailRender id={id} />
                        ) : null}
                    </div>
                    <div className="flex flex-col w-full shrink min-w-0 overflow-hidden">
                        <small className="text-gray-100 truncate">{fullName}</small>
                        <LayerNameComponent id={id} size="md" useColorClassNames={false} />
                        <div className="flex justify-between gap-1 mt-1 text-gray-300">
                            <small>
                                {formatDate({
                                    value: entity.createdAt * 1000,
                                    format: DateFormat.MMMDDYYYY,
                                    omitSuffix: true,
                                })}
                            </small>
                            <small>{usageString}</small>
                        </div>
                    </div>
                </div>
                {ProgressComponent && (
                    <div className="pl-[60px]">
                        <ProgressComponent id={id} isVisible={!!isVisible} />
                    </div>
                )}
            </div>
        )
    }
    return GenericAssetsListItem
}
